import React, { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import useProfileCompletion from "../../profiles/ChecklistCard/use_profile_completion";

const ProductContext = React.createContext(null);

// The products view builds both aggregates with json_agg over LEFT JOINs, so a product with no
// representatives (or no contact info) arrives as a one-element array of all-null placeholders
// rather than an empty array. Drop those before anything counts them, or an unrepresented product
// looks like it has one representative.
function realRepresentatives(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => row && (row.representation_id != null || row.profile_id != null));
}

function realContactDetails(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => row && row.type && row.value);
}

// The detail columns all have non-empty defaults (`[]`, `0`, `false`), so "the product says
// something here" has to be asked per type — a truthiness test would either render empty tiles or
// suppress real values (a price of 0, for one).
function hasItems(value) {
  return Array.isArray(value) && value.length > 0;
}

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function hasValue(value) {
  return value != null;
}

// derived_tags fans a hashtag out per tagged object and per representing profile, so one product
// carries the same hashtag several times over. Fold to one entry per hashtag with a count of how
// often it was tagged, most-tagged first, so a capped chip row means "the most-tagged ones" rather
// than "whichever the API returned first". (Same shaping as the person providers.)
function dedupeTags(rows) {
  return rows
    .reduce((acc, current) => {
      const existing = acc.find((item) => item.hashtag_id === current.hashtag_id);
      if (existing) {
        existing.times_tagged += 1;
      } else {
        acc.push({ ...current, times_tagged: 1 });
      }
      return acc;
    }, [])
    .sort((a, b) => b.times_tagged - a.times_tagged);
}

function ProductContextProvider(props) {
  const { children } = props;

  // ViewerContext seed ({ editable, isAuthenticated, planPaid, role }) computed server-side and
  // threaded ERB -> provider -> affordances. Default {} so a missing prop reads as non-editable.
  // eslint-disable-next-line react/destructuring-assignment
  const { viewerContext = {} } = props.value;

  // alphabetized
  const [asyncError, setAsyncError] = useState(null);
  const [generalError, setGeneralError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(null);
  const [product, setProduct] = useState({});
  const [productId, setProductId] = useState(null);
  const [productTags, setProductTags] = useState([]);
  const [representativesHydration, setRepresentativesHydrationRaw] = useState({ settled: false, visibleCount: 0 });
  const [topLevelIsLoading, setTopLevelIsLoading] = useState(true);

  // What the representative list reports back after hydrating: how many of the raw representatives
  // this viewer may actually see. Bails out when nothing changed so the reporting effect can fire
  // on every render of the list without churning the whole page.
  const setRepresentativesHydration = useCallback(({ settled, visibleCount }) => {
    setRepresentativesHydrationRaw((previous) =>
      previous.settled === settled && previous.visibleCount === visibleCount ? previous : { settled, visibleCount },
    );
  }, []);

  // The full product read. `silent` skips the page-level loading flag so a save- or
  // representation-triggered refetch doesn't flash the page skeleton over an already-rendered page.
  const loadProduct = useCallback(
    async ({ silent = false } = {}) => {
      if (!productId) return;
      try {
        if (!silent) setTopLevelIsLoading(true);
        qahoy.track("Products#products.get");

        // The tags decide whether the Overview card exists at all, so they are awaited alongside the
        // product rather than filled in afterwards — otherwise a product whose only Overview content
        // is its tags paints without that card and grows one a moment later. They stay enrichment
        // even so: a failed read costs the row, never the page.
        const tagsPromise = qapi.hashtags
          .getAllForPromoEntitiesForDisplay([productId], "Id::PromoEntity::Product")
          .then((tagsResponse) => dedupeTags(Array.isArray(tagsResponse.data) ? tagsResponse.data : []))
          .catch(() => []);

        const [response, tags] = await Promise.all([qapi.products.get(productId), tagsPromise]);
        setProductTags(tags);

        if (!response.data) throw new Error(JSON.stringify(response.error));

        const row = response.data[0];
        if (row) setProduct(row);
      } catch (error) {
        setAsyncError(error);
      } finally {
        if (!silent) setTopLevelIsLoading(false);
      }
    },
    [productId],
  );

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  // The refetch every successful write calls: same read, no skeleton.
  const silentReload = useCallback(() => loadProduct({ silent: true }), [loadProduct]);

  const editable = !!viewerContext.editable;
  const contactDetails = useMemo(() => realContactDetails(product.contact_details), [product.contact_details]);
  const representatives = useMemo(() => realRepresentatives(product.representatives), [product.representatives]);

  // Owner/admin-only widget: idling the hook (rather than just gating the render) for a
  // non-editable viewer means it never even issues the read. Mirrors Expert.jsx's own wiring of
  // useProfileCompletion — same hook, this entity's own completion-scores client and id.
  const {
    score: checklistScore,
    isLoading: checklistIsLoading,
    hidden: checklistHidden,
    hide: hideChecklist,
  } = useProfileCompletion({
    resourceClient: qapi.productCompletionScores,
    id: editable ? product?.id : null,
    kind: "product",
    refreshKey: product,
  });
  const checklistContributes = editable && !checklistHidden && (checklistIsLoading || !!checklistScore);

  // A refetched representative set has to be re-hydrated before its visible count means anything
  // again — without this an add/remove would be judged against the previous read's outcome.
  useEffect(() => {
    setRepresentativesHydration({ settled: false, visibleCount: 0 });
  }, [representatives, setRepresentativesHydration]);

  // Specifications is show-side gated by BOTH the page_config flag and the presence of content;
  // an editor missing either one gets the add-section affordance instead of an empty section.
  const showSpecifications = !!product.page_config?.show_specifications && !!product.subtype_attributes?.specifications;
  const canAddSpecifications = editable && !showSpecifications;

  // Which detail tiles have something to say, and therefore which of the three detail cards is
  // worth a card at all — most migrated products carry none of these fields, so every card
  // collapses rather than standing empty. `product_made_from_label` is deliberately absent: it only
  // names the Ingredients/Materials/Made-From tile that `product_made_from` fills, so on its own
  // (it defaults to 0) it would conjure a Manufacturing card for a product that has no such data.
  // Overview also counts its tags row, since a product can have tags and none of the four facts.
  const showCompanyTile = hasText(product.company_name);
  const showWebsiteTile = hasText(product.product_url);
  const showPriceTile = hasValue(product.price);
  const showCategoryTile = hasItems(product.product_category_names);
  const showStoresTile = hasItems(product.product_available_at_stores);
  const showSizesTile = hasItems(product.product_sizes);
  const showColorsTile = hasItems(product.product_colors);
  const showMadeFromTile = hasText(product.product_made_from);
  const showCountryTile = hasText(product.product_manufacturing_country_code);
  const showReleasedAtTile = hasValue(product.product_released_at);
  const showTagsRow = productTags.length > 0;
  const showDescription = hasText(product.description);

  const showOverviewCard =
    showCompanyTile || showWebsiteTile || showPriceTile || showCategoryTile || showTagsRow || showDescription;
  const showRetailCard = showStoresTile || showSizesTile || showColorsTile;
  const showManufacturingCard = showMadeFromTile || showCountryTile || showReleasedAtTile;

  // Which right-rail cards this viewer gets. Derived here so the page (which decides whether to
  // reserve the rail column at all) and the rail itself can never disagree.
  const showConnectCard = contactDetails.length > 0;
  // Samples availability is a real boolean status: only `true` earns the card. An unavailable
  // product says nothing about samples rather than advertising their absence.
  const showSamplesCard = product.product_samples_available === true;
  // Requesting a sample is a reporter's affordance — the people samples exist for.
  const canRequestSample = showSamplesCard && !!viewerContext.isAuthenticated && viewerContext.role === "media";
  const showCompanyCard = !!product.company_name;
  // An editor always gets this card: its Add affordance renders even with zero representatives. For
  // everyone else the raw aggregate only earns the card until hydration settles — the read applies
  // profile-visibility policies the aggregate doesn't, so a product whose every representative is
  // filtered out would otherwise keep an empty card (and an otherwise-empty rail column) forever.
  const showRepresentedByCard =
    editable ||
    (representatives.length > 0 && (!representativesHydration.settled || representativesHydration.visibleCount > 0));

  // Values and functions made available to consumers of the product context
  // (Memoized based on state values to avoid triggering unnecessary component rerenders)
  const providerValue = useMemo(
    () => ({
      // alphabetized
      asyncError,
      canAddSpecifications,
      canRequestSample,
      checklistContributes,
      checklistHidden,
      checklistIsLoading,
      checklistScore,
      contactDetails,
      generalError,
      hideChecklist,
      isUpdating,
      openEditModal,
      product,
      productId,
      productTags,
      representatives,
      setAsyncError,
      setGeneralError,
      setIsUpdating,
      setOpenEditModal,
      setProductId,
      setRepresentativesHydration,
      showCategoryTile,
      showColorsTile,
      showCompanyCard,
      showCompanyTile,
      showConnectCard,
      showCountryTile,
      showDescription,
      showMadeFromTile,
      showManufacturingCard,
      showOverviewCard,
      showPriceTile,
      showReleasedAtTile,
      showRepresentedByCard,
      showRetailCard,
      showSamplesCard,
      showSizesTile,
      showSpecifications,
      showStoresTile,
      showTagsRow,
      showWebsiteTile,
      silentReload,
      topLevelIsLoading,
      viewerContext,
    }),
    [
      asyncError,
      canAddSpecifications,
      canRequestSample,
      checklistContributes,
      checklistHidden,
      checklistIsLoading,
      checklistScore,
      contactDetails,
      generalError,
      hideChecklist,
      isUpdating,
      openEditModal,
      product,
      productId,
      productTags,
      representatives,
      setRepresentativesHydration,
      showCategoryTile,
      showColorsTile,
      showCompanyCard,
      showCompanyTile,
      showConnectCard,
      showCountryTile,
      showDescription,
      showMadeFromTile,
      showManufacturingCard,
      showOverviewCard,
      showPriceTile,
      showReleasedAtTile,
      showRepresentedByCard,
      showRetailCard,
      showSamplesCard,
      showSizesTile,
      showSpecifications,
      showStoresTile,
      showTagsRow,
      showWebsiteTile,
      silentReload,
      topLevelIsLoading,
      viewerContext,
    ],
  );

  return <ProductContext.Provider value={providerValue}>{children}</ProductContext.Provider>;
}

export { ProductContext, ProductContextProvider };

ProductContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
